import { useState } from 'react';
import { toast } from '../../lib/toast';
import styles from './GuideSelect.module.css'
import step1 from './images/step1Copilot.png';
import step2 from './images/step2Copilot.png';
import step3 from './images/step3Copilot.png';
import step4 from './images/step4Copilot.png';
import step5 from './images/step5Copilot.png';
import step6_1 from './images/step6Copilot1.png';
import step6_2 from './images/step6Copilot2.png';
import step7 from './images/step7Copilot.png';
import { Copy } from 'lucide-react';
import ImageModal from '../../ui/imageModal/ImageModal';

const instructionsText = `
You are an AI assistant integrated with systems via the MCP platform. 
Use the available tools to assist users with their related tasks. 
Always ensure data accuracy and follow best practices for operations.
- Serve as an interface for users to interact with MCP server tools, enabling operations and actions directly from the tools.
- Receive and process responses from MCP server tools, which are always returned in a specific JSON pattern.
- Extract the 'parameters' field from the JSON response and convert it into a clear, readable answer for the user.
- Clearly indicate success or error based on the 'ok' and 'message' fields in the response.
- Avoid displaying raw JSON to users; always provide a user-friendly summary of the operation's result.
- Ensure that all responses are easy to understand, regardless of the complexity of the parameters.
- Do not perform any actions outside of interacting with MCP server tools and summarizing their responses.
- Maintain a professional and helpful tone in all communications.
- Do not process or display any information not included in the MCP response pattern.
- Never repeat the same answer in consecutive messages.
- Decline to answer questions unrelated to MCP server tool operations or responses.`;

export default function ConnectCopilot() {
    const [mcpServerUrl] = useState(`${import.meta.env.VITE_API_OAUTH_BASE_URL}/mcp`)
    const [modalImg, setModalImg] = useState<string | null>(null);


    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        toast.success(`${label} copied to clipboard!`)
    }

    return (
        <div className={styles.container}>

            <div className={styles.header}>
                <h1>Connect Copilot to MCP</h1>
                <p>Follow these visual steps to connect your tools to Copilot</p>
            </div>

            <div className={styles.steps}>
                {/* Step 1: Open Copilot Settings & Go to Connectors */}
                <div className={styles.step}>
                    <div className={styles.stepNumber}>1</div>
                    <div className={styles.stepContent}>
                        <h2>Open Copilot Studio and Go to Agents</h2>
                        <p>
                            Click on <strong>New Agent</strong>.
                        </p>
                        <div className={styles.imageContainer}>
                            <img
                                src={step1}
                                alt="Open Copilot Settings"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step1)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 1: Open Settings%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Step 4: Configure Connector */}
                <div className={styles.step}>
                    <div className={styles.stepNumber}>2</div>
                    <div className={styles.stepContent}>
                        <h2>Configure Your Agent</h2>
                        <p>Click on <strong>Configure</strong> tab</p>
                        <p>Set the agent name to <code>MCP Agent</code> and paste the instructions</p>
                        <p>Then click on <strong>Create</strong> button</p>

                        <div className={styles.configForm}>
                            <div className={styles.formGroup}>
                                <label>Agent Name:</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={'MCP Agent'}
                                        disabled={true}
                                        className={styles.input}
                                    />
                                    <button
                                        onClick={() => copyToClipboard('MCP Agent', 'Agent Name')}
                                        className={styles.copyBtn}
                                    ><Copy size={16} /></button>
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Instructions:</label>
                                <div className={styles.inputGroup}>
                                    <textarea
                                        value={instructionsText}
                                        className={styles.input}
                                        disabled={true}
                                    />
                                    <button
                                        onClick={() => copyToClipboard(instructionsText, 'Instructions')}
                                        className={styles.copyBtn}
                                    ><Copy size={16} /></button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.imageContainer}>
                            <img
                                src={step2}
                                alt="Configure MCP Connector"
                                onClick={() => setModalImg(step2)}
                                className={styles.stepImage}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 4: Configure Connector%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.step}>
                    <div className={styles.stepNumber}>3</div>
                    <div className={styles.stepContent}>
                        <h2>Create MCP tools</h2>
                        <p>Click on <strong>Tools</strong> tab.</p>
                        <p>Click on <strong>Add a Tool</strong> button.
                        </p>
                        <div className={styles.imageContainer}>
                            <img
                                src={step3}
                                alt="Configure MCP Connector"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step3)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 4: Configure Connector%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.step}>
                    <div className={styles.stepNumber}>4</div>
                    <div className={styles.stepContent}>
                        <h2>Add Tools in Copilot</h2>
                        <p>In the open modal select <strong>New tool</strong> </p>
                        <p>Then select the <strong>Model Context Protocol</strong> type.</p>
                        <div className={styles.imageContainer}>
                            <img
                                src={step4}
                                alt="Open Tools Section"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step4)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 5: View Tools%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.step}>
                    <div className={styles.stepNumber}>5</div>
                    <div className={styles.stepContent}>
                        <h2>Configure Your MCP Connector</h2>
                        <p>Set the connector name to <code>mcp</code> and paste your server URL</p>

                        <div className={styles.configForm}>
                            <div className={styles.formGroup}>
                                <label>Connector Name:</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={'MCP Tools'}
                                        disabled={true}
                                        className={styles.input}
                                    />
                                    <button
                                        onClick={() => copyToClipboard('MCP Tools', 'Connector Name')}
                                        className={styles.copyBtn}
                                    ><Copy size={16} /></button>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label>MCP Server URL:</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="text"
                                        value={mcpServerUrl || 'https://your-mcp-server.com/mcp'}
                                        className={styles.input}
                                        disabled={true}
                                    />
                                    <button
                                        onClick={() => copyToClipboard(mcpServerUrl || 'https://your-mcp-server.com/mcp', 'MCP Server URL')}
                                        className={styles.copyBtn}
                                    ><Copy size={16} /></button>
                                </div>
                            </div>
                            <p>In Authentication select <strong>OAuth 2.0</strong> and type <strong>dynamic discovery</strong> for the Grant Type</p>

                        </div>
                        <p>Click on <strong>Create</strong> button to finish the setup</p>

                        <div className={styles.imageContainer}>
                            <img
                                src={step5}
                                alt="Configure MCP Connector"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step5)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 4: Configure Connector%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Step 6: View Available Tools */}
                <div className={styles.step}>
                    <div className={styles.stepNumber}>6</div>
                    <div className={styles.stepContent}>
                        <h2>Create connection</h2>
                        <p>Select the <strong>Create new connection</strong></p>
                        <p>Sign in with Your credentials</p>

                        <div className={styles.imageContainer}>
                            <img
                                src={step6_1}
                                alt="Available Tools List"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step6_1)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 6: Available Tools%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                        <div className={styles.imageContainer}>
                            <img
                                src={step6_2}
                                alt="Available Tools List"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step6_2)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 6: Available Tools%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                        <p>After Login Click on<strong>Add to agent</strong></p>

                    </div>
                </div>

                {/* Step 7: Try Using Tools */}
                <div className={styles.step}>
                    <div className={styles.stepNumber}>7</div>
                    <div className={styles.stepContent}>
                        <h2>Try Using a Tool</h2>
                        <p>Click on the agent, and try using the Tools</p>

                        <div className={styles.imageContainer}>
                            <img
                                src={step7}
                                alt="Using MCP Tools"
                                className={styles.stepImage}
                                onClick={() => setModalImg(step7)}
                                onError={(e) => {
                                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 7: Try a Tool%3C/text%3E%3C/svg%3E'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.successCard}>
                <h2>🎉 You're All Set!</h2>
                <p>Copilot can now use your tools through the MCP connector. Start chatting and let Copilot help you with operations!</p>
            </div>

            <div className={styles.troubleshooting}>
                <h2>Need Help?</h2>
                <div className={styles.troubleItem}>
                    <h3>❌ Copilot doesn't see my tools</h3>
                    <ul>
                        <li>Verify the MCP server URL is correct and accessible</li>
                        <li>Ensure your tools are enabled in the Tools section of this app</li>
                        <li>Restart Copilot completely</li>
                    </ul>
                </div>
                <div className={styles.troubleItem}>
                    <h3>⚠️ Tool execution fails</h3>
                    <ul>
                        <li>Test your connection in the Connections section</li>
                        <li>Verify your credentials are correct</li>
                        <li>Check that CSRF tokens are properly configured</li>
                    </ul>
                </div>
            </div>
            {modalImg && (
                <ImageModal src={modalImg} alt="Step" onClose={() => setModalImg(null)} />
            )}
        </div>
    )
}
